<template>
  <v-container fluid>
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="triggersHistoriesData"
        :options.sync="options"
        item-key="_id"
        :search="search"
        :footer-props="footerProps"
        :loading-text="$t('triggersHistories.triggersHistoriesLoading')"
        :no-data-text="$t('triggersHistories.noTriggersHistories')"
        mobile-breakpoint="768"
        :loading="loadingData"
        :server-items-length="totalTriggersHistories"
        dense
        :header-props="headerProps"
        show-expand
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-badge overlap :value="nbActiveFilters" :content="nbActiveFilters">
              <v-btn color="primary" outlined class="ml-2" @click="displayFilters = !displayFilters">
                <v-icon>mdi-filter-plus-outline</v-icon>
              </v-btn>
            </v-badge>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined class="mb-2" @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip
            :color="getTriggerStatusBackgroundColor(item.status)"
            :text-color="getTriggerStatusTextColor(item.status)"
            small
          >
            {{ getStatusTranslation(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.date="{ item }">
          <span>{{ new Date(item.date) | moment('DD/MM/YYYY HH:mm:ss') }}</span>
        </template>
        <template v-slot:item.action="{ item }">
          <v-btn
            :class="[$vuetify.breakpoint.width >= 481 ? '' : 'pr-0']"
            text
            color="primary"
            @click="executeAction(item._id)"
          >
            <v-icon v-if="$vuetify.breakpoint.width > 767 && $vuetify.breakpoint.width < 1264">mdi-magnify</v-icon>
            <span v-else>{{ $t('triggersHistories.details') }}</span>
          </v-btn>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="px-8 py-2 data_table_header_color">
            <trigger-calls-panel :calls="item.trigger.trigger_calls" />
          </td>
        </template>
      </v-data-table>
      <filter-dialog
        :key="formFiltersKey"
        v-model="displayFilters"
        :title="$t('triggersHistories.filterTriggersHistories')"
        :fields="filtersFields"
        @confirm="filter"
        :loadingData="loadingData"
      />
    </v-card>
  </v-container>
</template>

<script>
  import { EventBus } from '../../event-bus'
  import { mapState } from 'vuex'
  import TriggerCallsPanel from '../../components/triggersHistories/TriggerCallsPanel.vue'
  import { getTriggerStatusBackgroundColor, getTriggerStatusTextColor } from '../../helpers/helper'

  export default {
    name: 'TriggersHistoriesTable',
    components: { TriggerCallsPanel },
    data() {
      return {
        search: '',
        triggersHistoriesData: [],
        options: {
          itemsPerPage: 15,
          sortDesc: [],
        },
        loadingData: false,
        totalTriggersHistories: 0,
        filters: [],
        displayFilters: false,
        modalTitle: '',
        modalText: '',
        formFiltersKey: Date.now(),
      }
    },
    methods: {
      async reloadList() {
        await this.$auth.isAuthenticated()
        EventBus.$emit('reLoadCounter')
        this.options.page = 1
        this.list()
      },
      filter(filters) {
        this.filters = filters
        this.reloadList()
      },
      list() {
        this.loadingData = true
        let { page, itemsPerPage, sortDesc, sortBy } = this.options

        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            page: page,
            items_per_page: itemsPerPage,
            sort_by: sortBy.length === 0 ? undefined : sortBy,
            sort_desc: sortDesc.length === 0 ? true : sortDesc,
          },
        }
        for (let filter of this.filters) {
          if (filter.active) {
            myInit.queryStringParameters[filter.name] = filter.value
          }
        }
        this.$Amplify.API.get('triggers', '/v1/int/triggers-histories', myInit)
          .then((res) => {
            this.triggersHistoriesData = res.data
            this.totalTriggersHistories = res.nbTriggersHistories
            if (this.totalTriggersHistories < (this.options.page - 1) * this.options.itemsPerPage + 1) {
              let pageNumber = Math.floor(this.totalTriggersHistories / this.options.itemsPerPage)
              this.options.page = pageNumber === 0 ? 1 : pageNumber
            }
            this.loadingData = false
          })
          .catch((e) => {
            this.loadingData = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('triggersHistories.errorTriggersHistoriesLoading'),
              color: 'error',
            })
          })
      },
      getStatusTranslation: function (statusValue) {
        const status = this.status.find((status) => status.value === statusValue)
        if (status) {
          return status.text
        } else {
          return statusValue
        }
      },
      getTriggerStatusBackgroundColor,
      getTriggerStatusTextColor,
      executeAction: function (id) {
        this.$router.push({ path: `/triggers-histories/${id}` })
      },
    },
    watch: {
      options: {
        handler() {
          this.list()
        },
      },
    },
    computed: {
      ...mapState(['currentStore', 'isCustomerService', 'roleRights']),

      nbActiveFilters() {
        return this.filters.filter((filter) => filter.active).length
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('triggersHistories.triggersHistoriesPerPage'),
        }
      },
      headerProps() {
        return {
          sortByText: this.$t('triggersHistories.sortByText'),
        }
      },
      headers() {
        return [
          {
            text: this.$t('triggersHistories.reference'),
            value: '_id',
            filterType: 'text',
          },
          {
            text: this.$t('triggersHistories.name'),
            value: 'trigger.trigger_name',
            filterType: 'text',
          },
          {
            text: this.$t('triggersHistories.sourceEntity'),
            value: 'source_entity.entity_id',
            filterType: 'text',
          },
          {
            text: this.$t('triggersHistories.date'),
            value: 'date',
            filterType: 'date',
          },
          {
            text: this.$t('triggersHistories.status'),
            value: 'status',
            filterType: 'array',
            clearable: true,
            arrayValues: this.status,
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
          {
            text: '',
            value: 'data-table-expand',
            sortable: false,
          },
        ]
      },
      status() {
        return [
          { text: this.$t('triggersHistories.processing'), value: 'processing' },
          { text: this.$t('triggersHistories.success'), value: 'success' },
          { text: this.$t('triggersHistories.error'), value: 'error' },
          { text: this.$t('triggersHistories.relaunched'), value: 'relaunched' },
          { text: this.$t('triggersHistories.relaunchFailed'), value: 'relaunch_failed' },
        ]
      },
      filtersFields() {
        return [
          {
            text: this.$t('triggersHistories.reference'),
            value: 'reference',
            filterType: 'text',
          },
          {
            text: this.$t('triggersHistories.name'),
            value: 'trigger.trigger_name',
            filterType: 'text',
          },
          {
            text: this.$t('triggersHistories.sourceEntity'),
            value: 'source_entity.entity_id',
            filterType: 'text',
          },
          {
            text: this.$t('triggersHistories.status'),
            value: 'status',
            filterType: 'array',
            clearable: true,
            arrayValues: this.status,
          },
        ]
      },
    },
    activated() {
      let previousPath = this.$store.state.previousPath.split('/')[1]
      // we reset filters if the previous page wasn't the details of a trigger
      if (previousPath !== 'triggers-histories') {
        this.filters = []
        this.formFiltersKey = Date.now()
        this.options.sortBy = []
        this.options.sortDesc = []
        this.options.page = 1
        this.options.itemsPerPage = 15
      }
      this.triggersHistoriesData = []
      this.list()
    },
  }
</script>

<style scoped>
  ::v-deep tr.v-data-table__expanded__content {
    box-shadow: none !important;
  }

  ::v-deep .v-data-table--mobile tr.v-data-table__expanded__content > td {
    display: block;
    width: 100%;
    height: auto;
  }
</style>
